import debounce from 'hs-lodash/debounce';
import isEmpty from 'hs-lodash/isEmpty';
import { usageTrackerMiddleware } from 'usage-tracker-redux';
import { getActiveExperimentsParameters } from '../experiments/ExperimentUtils';
import { stepsWithNoAlert } from '../routing/alertBeforeUnload';
import { getQueryParams } from '../routing/getQueryParams';
import { getResurrectionAttempt } from '../routing/getResurrectionAttempt';
import { Data } from '../signup-data/DATA';
import { hasPresetOptionalDomain } from '../step-company-domain/companyDomainUtils';
import { getDataKeysByStep } from '../step/getStepData';
import { AppActionTypes } from '../store/app/action-types';
import { getIsNewPortalCreated, getIsNewUser } from '../store/app/selectors';
import { AuthActionTypes } from '../store/auth/action-types';
import { ErrorActionTypes } from '../store/error/action-types';
import { getActiveError } from '../store/error/selectors';
import { ExperimentActionTypes } from '../store/experiments/action-types';
import { FlowActionTypes } from '../store/flow/action-types';
import { getCurrentStep, getNextSteps } from '../store/flow/selectors';
import { getActiveLoading } from '../store/loading-status/selectors';
import { getPreAccountCreationSurveyData, getSignupDataSkipped, getSignupDataValue, getSurveyData } from '../store/signup-data/selectors';
import { TrackingActionTypes } from '../store/tracking/action-types';
import { getStepConfig } from '../views/getStepConfig';
import { visitorContextActionHandlers } from '../visitor-context-fetch/visitorContextActionHandlers';
import AnonymousUsageTrackerContainer from './AnonymousUsageTrackerContainer';
import { errorsTrackingHandler } from './errorsTrackingHandler';
import { EventName } from './EventName';
import { getTracker } from './getTracker';
import { oAuthActionHandlers } from './oAuthActionHandlers';
import { stepCompletedTrackingHandler } from './stepCompletedTrackingHandler';
import UsageTrackerContainer from './UsageTrackingContainer';
/**
 * @description Tracking Middleware - if any of the below actions are dispatched,
 * a correspoinding event will be tracked, or added to the queue.
 */

export const trackEvent = (name, properties = {}, sendAsBeacon = false) => {
  getTracker().track(name, properties, sendAsBeacon);
};
export const trackInteraction = ({
  action,
  properties = {},
  sendAsBeacon = false
}) => trackEvent(EventName.Interaction, Object.assign({
  action
}, properties), sendAsBeacon);
const actionHandlers = Object.assign({}, oAuthActionHandlers, {}, visitorContextActionHandlers, {
  [FlowActionTypes.StepCompleted]: (action, state, dispatch) => {
    const currentStep = getCurrentStep(state);
    const currentError = getActiveError(state);
    const currentLoading = getActiveLoading(state);
    const nextSteps = getNextSteps(state);
    const nextStepName = !isEmpty(nextSteps) ? nextSteps[0] : null;
    let properties = {};

    if (currentStep) {
      const stepConfig = getStepConfig(currentStep);
      const stepName = currentStep || currentError || currentLoading || 'step-unknown';
      properties = Object.assign({
        stepName
      }, nextStepName && {
        nextStepName
      }); // Include any extra step properties that should be tracked

      if (stepConfig && stepConfig.getStepPropertiesToTrack) {
        properties = Object.assign({}, properties, {}, stepConfig.getStepPropertiesToTrack(state));
      } // Include the selected option for this step.


      if (stepConfig && stepConfig.trackSelectedOption) {
        const dataKeys = getDataKeysByStep([currentStep]);
        dataKeys.forEach(dataKey => {
          const value = getSignupDataValue(state, dataKey);
          properties = Object.assign({}, properties, {
            selectedOption: value
          });
        });
      }

      const trackingHandler = stepCompletedTrackingHandler(currentStep);

      if (trackingHandler) {
        trackingHandler(action, state, dispatch);
      }
    }

    trackEvent(EventName.StepCompleted, properties);
  },
  [ErrorActionTypes.SetError]: (action, state, dispatch) => {
    const activeError = getActiveError(state);

    if (activeError) {
      const trackingHandler = errorsTrackingHandler(activeError);

      if (trackingHandler) {
        trackingHandler(action, state, dispatch);
      }
    }
  },
  [AppActionTypes.SignupCompleted]: (action, state) => {
    const {
      theme
    } = getQueryParams();

    if (AnonymousUsageTrackerContainer.isAnonymousTrackingEnabled) {
      AnonymousUsageTrackerContainer.track(EventName.SignupCompletedAnonymous, {}, true);
    }

    const wasOptionalPhoneNumberStepSkipped = getSignupDataSkipped(state, Data.OptionalPhoneNumber);
    const resurrectionAttempt = getResurrectionAttempt();
    const activeExperimentParameters = getActiveExperimentsParameters(state);
    trackEvent(EventName.SignupCompleted, Object.assign({
      newUser: getIsNewUser(state),
      experimentParameter: activeExperimentParameters,
      doesNotHaveDomain: hasPresetOptionalDomain(state),
      resurrectionAttempt,
      phoneNumberSubmitted: !wasOptionalPhoneNumberStepSkipped,
      hasBeenAnonymouslyTracked: AnonymousUsageTrackerContainer.isAnonymousTrackingEnabled,
      theme
    }, getPreAccountCreationSurveyData(state)), true);
  },
  [TrackingActionTypes.SurveyInteraction]: (action, state) => {
    trackEvent(EventName.SurveyInteraction, Object.assign({
      action: action.message
    }, getSurveyData(state), {
      experimentParameter: getActiveExperimentsParameters(state)
    }), true);
  },
  [TrackingActionTypes.SignupInitiated]: () => {
    const {
      theme
    } = getQueryParams();
    trackEvent(EventName.SignupInitiated, {
      theme
    }, true);
  },
  [TrackingActionTypes.AuthState]: action => {
    trackEvent(EventName.AuthState, {
      action: 'auth state',
      eventSubtype: action.message
    });
  },
  [TrackingActionTypes.SignupInteraction]: action => {
    trackInteraction(action.payload);
  },
  [TrackingActionTypes.StepView]: action => {
    trackEvent(EventName.View, {
      screen: action.screen,
      stepName: action.screen
    });
  },
  [TrackingActionTypes.MessagingScrolling]: debounce(action => {
    trackEvent(EventName.MessagingScrolling, {
      action: 'messaging-scrolling',
      stepName: action.screen,
      percentage: action.percentage
    });
  }, 300),
  [TrackingActionTypes.Click]: action => {
    trackEvent(EventName.Click, {
      action: action.message,
      linkLocation: action.linkLocation
    });
  },
  [TrackingActionTypes.LeavePage]: (action, state) => {
    const currentStep = getCurrentStep(state);
    const shouldTrack = currentStep && !stepsWithNoAlert.includes(currentStep) && !getActiveError(state);

    if (shouldTrack) {
      trackInteraction({
        action: action.message,
        properties: {
          leavingStep: currentStep
        },
        sendAsBeacon: true
      });
    }
  },
  [TrackingActionTypes.FormError]: action => {
    trackEvent(EventName.FormError, {
      eventSubtype: action.eventSubtype,
      formError: action.formError
    });
  },
  [TrackingActionTypes.BackButton]: action => {
    trackEvent(EventName.BackButton, {
      action: 'back-button-clicked',
      fromStep: action.fromStep,
      toStep: action.toStep
    });
  },
  [TrackingActionTypes.CreationError]: action => {
    trackEvent(EventName.CreationError, {
      action: action.actionMessage,
      statusMessage: action.statusMessage
    });
  },
  [TrackingActionTypes.UtmVerification]: action => {
    trackEvent(EventName.UtmVerification, {
      action: 'utm-verification',
      utmMedium: action.utmMedium,
      utmSource: action.utmSource
    });
  },
  [AuthActionTypes.ExistingUser]: () => {
    trackInteraction({
      action: 'selected existing user'
    });
  },
  [AuthActionTypes.ClickCreateNewAccountSameUser]: action => {
    trackInteraction({
      action: action.hubless ? 'hubless-selected-create-new-account' : 'selected-create-new-portal'
    });
  },
  [TrackingActionTypes.VerifyIdentityCompleted]: action => {
    const resurrectionAttempt = getResurrectionAttempt();
    trackEvent(EventName.VerifyIdentityCompleted, {
      verificationMethod: action.verificationMethod,
      resurrectionAttempt
    });
  },
  [TrackingActionTypes.VerifyIdentityStarted]: action => {
    trackEvent(EventName.VerifyIdentityStarted, {
      verificationMethod: action.verificationMethod
    });
  },
  [TrackingActionTypes.SignupRedirect]: (action, state) => {
    const newPortal = getIsNewPortalCreated(state);
    const newUser = getIsNewUser(state);
    trackEvent(EventName.SignupRedirect, {
      url: action.url,
      newUser,
      newPortal
    }, true);
  },
  [ExperimentActionTypes.TrackExposure]: action => {
    trackEvent(EventName.ExperimentExposure, {
      action: action.message
    }, true);
  },
  [TrackingActionTypes.InvalidDomain]: action => {
    trackEvent(EventName.InvalidDomainCollected, {
      action: 'invalid-domain',
      invalidDomain: action.invalidDomain
    });
  },
  [TrackingActionTypes.SurveyCompleted]: (action, state) => {
    trackEvent(EventName.SurveyCompleted, Object.assign({}, getSurveyData(state), {
      experimentParameter: getActiveExperimentsParameters(state)
    }), true);
  },
  [TrackingActionTypes.LoginSuccess]: action => {
    trackInteraction({
      action: 'login-success',
      properties: {
        totalNumberOfCalls: action.numOfRetries
      },
      sendAsBeacon: true
    });
  },
  [TrackingActionTypes.IndustryCollected]: action => {
    trackEvent(EventName.IndustryCollected, {
      industryName: action.industryName,
      industryId: action.industryId,
      userInput: action.userInput
    });
  },
  [ExperimentActionTypes.UnenrollExperiment]: action => {
    trackInteraction({
      action: `${action.experiment}-unenrolled`
    });
  },
  [TrackingActionTypes.SignupPageView]: action => {
    trackEvent(EventName.SignupPageView, {
      action: action.action
    });
  }
});
export const UsageTrackingMiddleware = usageTrackerMiddleware(UsageTrackerContainer.getTracker(), actionHandlers);